// @ts-nocheck
import * as React from "react";
import { Ic, useCurrentUser, apiErrorMessage } from "./common";
import { orgAvailability } from "../api";

// 유관기관 담당자용 모바일 — "30초 갱신" (/m/org).
//
// 자원은행의 성패는 화면의 완성도가 아니라 **기관이 실제로 갱신하는가**로 갈린다.
// 그래서 이 화면의 설계 목표는 하나다: 알림을 받고 들어와 30초 안에 끝내기.
//   · 급여 카드 하나당 큰 버튼 3개(가능·협의·불가) — 누르면 그 자리에서 바로 저장.
//   · 여유 인원·개시일·메모는 접어 두고, 필요할 때만 편다.
//   · 확인필요(오래된 보고)를 맨 위로 올려 "지금 손봐야 할 것"부터 보이게 한다.
//
// 데이터: GET/POST /api/v1/organization_services (서버가 자기 기관으로 스코프)
// 권한: can_update_org_availability (agency·region_admin·system_admin)

const STATUS_ORDER = ["available", "limited", "unavailable"];
const STATUS_META = {
  available:   { label: "가능",     color: "green", desc: "지금 모실 수 있어요" },
  limited:     { label: "협의",     color: "amber", desc: "조건 맞으면 가능해요" },
  unavailable: { label: "불가",     color: "rose",  desc: "지금은 어려워요" },
};
// 조회 시 계산되는 표시 상태(서버 effective_status)의 색. 보고 3종 + 파생 3종.
const EFFECTIVE_COLOR = {
  available: "green", limited: "amber", unavailable: "rose",
  stale: "orange", unknown: "gray", suspended: "gray",
};
// "지금 손봐야 할 것"이 위로 오도록. 확인필요·미등록 → 불가 → 협의 → 가능 → 중지.
const SORT_RANK = { stale: 0, unknown: 1, unavailable: 2, limited: 3, available: 4, suspended: 5 };

const DAY_LABEL  = { mon:"월", tue:"화", wed:"수", thu:"목", fri:"금", sat:"토", sun:"일" };
const SLOT_LABEL = { morning:"오전", afternoon:"오후", evening:"저녁", night:"야간" };

const StatusBar = () => (
  <div style={{display:'flex', justifyContent:'space-between', padding:'8px 22px 4px', fontSize:11, fontWeight:600, color:'var(--ink-1)', flex:'0 0 auto'}}>
    <span>{new Date().toLocaleTimeString('ko-KR',{hour:'2-digit',minute:'2-digit'})}</span>
    <div style={{display:'flex', gap:5, alignItems:'center'}}>
      <span>●●●</span><span style={{fontSize:10}}>5G</span>
      <span style={{width:18, height:9, border:'1px solid var(--ink-1)', borderRadius:2, position:'relative'}}><span style={{position:'absolute', inset:1, background:'var(--ink-1)'}}/></span>
    </div>
  </div>
);

// KST 날짜 문자열(YYYY-MM-DD). toISOString().slice() 는 UTC 로 밀려 하루 어긋난다.
function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

// 여러 기관을 걸쳐 보는 뷰의 부제. 권역명이 있으면 그것, 없으면 중립 문구.
function region_label(user) {
  return user?.region_name || '담당 권역';
}

function freshnessText(svc) {
  const d = svc.days_since_report;
  if (d == null) return "아직 보고 없음";
  if (d === 0)   return "오늘 갱신함";
  return `${d}일 전 갱신`;
}

export function OrgAvailability() {
  const { user, loaded } = useCurrentUser();
  const [rows, setRows]   = React.useState(null);
  const [err, setErr]     = React.useState(null);
  const [openId, setOpenId] = React.useState(null);   // 상세(여유·개시일·메모) 펼침
  const [saving, setSaving] = React.useState(null);   // 저장 중인 service id
  const [toast, setToast]   = React.useState(null);

  const load = React.useCallback(() => {
    orgAvailability.list()
      .then(d => setRows(d.organization_services || []))
      .catch(e => setErr(apiErrorMessage(e)));
  }, []);
  React.useEffect(() => { if (loaded && user) load(); }, [loaded, user, load]);

  // 토스트는 2.5초 뒤 자동 소멸.
  React.useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 2500);
    return () => window.clearTimeout(t);
  }, [toast]);

  // 한 번의 탭으로 상태를 보고한다. 상세를 펼쳐 둔 경우 그 값도 함께 실어 보낸다.
  const report = React.useCallback(async (svc, status, extra) => {
    if (saving) return;
    setSaving(svc.id);
    setErr(null);
    try {
      const updated = await orgAvailability.report(svc.id, {
        status,
        open_slots:     extra?.open_slots ?? undefined,
        available_from: extra?.available_from || undefined,
        note:           extra?.note || undefined,
      });
      setRows(prev => prev.map(r => (r.id === updated.id ? updated : r)));
      setOpenId(null);
      setToast(`${updated.service_label} · ${updated.status_label}로 갱신했어요`);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setSaving(null);
    }
  }, [saving]);

  const sorted = React.useMemo(() => {
    if (!rows) return null;
    return [...rows].sort((a, b) => {
      const ra = SORT_RANK[a.effective_status] ?? 9;
      const rb = SORT_RANK[b.effective_status] ?? 9;
      return ra !== rb ? ra - rb : a.service_label.localeCompare(b.service_label, "ko");
    });
  }, [rows]);

  const needsAttention = (sorted || []).filter(
    s => s.effective_status === "stale" || s.effective_status === "unknown",
  ).length;

  // 한 기관만 보는 뷰(기관 담당자)인지, 권역 전체를 보는 뷰(권역·시스템 관리자)인지.
  // 여러 기관이 섞여 있으면 카드마다 기관명이 없으면 어느 기관 건지 알 수 없다.
  const multiOrg = React.useMemo(() => {
    const ids = new Set((rows || []).map(r => r.organization_id));
    return ids.size > 1;
  }, [rows]);

  const subtitle = multiOrg
    ? `${region_label(user)} · 기관이 보고한 가용성`
    : `${user?.organization_name || '우리 기관'} · 지금 모실 수 있는지 알려주세요`;

  return (
    <div className="app" style={{background:'var(--bg)', overflow:'hidden', display:'flex', flexDirection:'column'}}>
      <StatusBar/>

      <div style={{padding:'10px 22px 6px', flex:'0 0 auto'}}>
        <div style={{fontSize:18, fontWeight:800}}>가용성 갱신</div>
        <div className="muted" style={{fontSize:12, marginTop:2}}>{subtitle}</div>
      </div>

      {!loaded && <div className="muted" style={{padding:'30px 22px', textAlign:'center', fontSize:12}}>불러오는 중…</div>}

      {loaded && !user && (
        <div style={{padding:'30px 22px', textAlign:'center'}}>
          <div className="muted" style={{fontSize:13, marginBottom:14}}>로그인이 필요합니다.</div>
          <a href="/login" className="btn btn-primary" style={{padding:'10px 22px', borderRadius:10, textDecoration:'none'}}>로그인</a>
        </div>
      )}

      {user && (
        <div style={{flex:1, overflow:'auto', padding:'4px 22px 22px'}}>
          {needsAttention > 0 && (
            <div style={{
              display:'flex', alignItems:'center', gap:9, padding:'11px 13px', marginBottom:12,
              background:'var(--orange-50)', border:'1px solid var(--orange-300)', borderRadius:12,
            }}>
              <span style={{width:18, height:18, fontSize:18, color:'var(--orange-600)', display:'grid', placeItems:'center'}}>{Ic.alert}</span>
              <div style={{fontSize:12.5, fontWeight:700, color:'var(--orange-700)'}}>
                {needsAttention}건 확인이 필요해요
              </div>
            </div>
          )}

          {err && (
            <div style={{padding:'11px 13px', marginBottom:12, background:'var(--rose-50)',
                         border:'1px solid var(--rose-300)', borderRadius:12,
                         fontSize:12.5, color:'var(--rose-700)', fontWeight:600}}>
              {err}
            </div>
          )}

          {sorted == null && !err && (
            <div className="muted" style={{padding:'24px 0', textAlign:'center', fontSize:12}}>불러오는 중…</div>
          )}

          {sorted && sorted.length === 0 && (
            <div style={{padding:'34px 16px', textAlign:'center'}}>
              <div style={{fontSize:13, fontWeight:700, marginBottom:6}}>등록된 급여가 없어요</div>
              <div className="muted" style={{fontSize:12, lineHeight:1.6}}>
                우리 기관이 제공하는 급여를 먼저 등록해야<br/>가용성을 알릴 수 있어요.
                담당 권역 관리자에게 요청해주세요.
              </div>
            </div>
          )}

          <div style={{display:'flex', flexDirection:'column', gap:11}}>
            {(sorted || []).map(svc => (
              <ServiceCard
                key={svc.id}
                svc={svc}
                open={openId === svc.id}
                busy={saving === svc.id}
                showOrg={multiOrg}
                onToggle={() => setOpenId(openId === svc.id ? null : svc.id)}
                onReport={(status, extra) => report(svc, status, extra)}
              />
            ))}
          </div>
        </div>
      )}

      {toast && (
        <div role="status" style={{
          position:'absolute', left:22, right:22, bottom:26, padding:'12px 15px',
          background:'var(--ink-1)', color:'var(--bg)', borderRadius:12,
          fontSize:12.5, fontWeight:700, textAlign:'center', boxShadow:'var(--shadow-md)',
        }}>{toast}</div>
      )}
    </div>
  );
}

function ServiceCard({ svc, open, busy, showOrg, onToggle, onReport }) {
  const [slots, setSlots] = React.useState(svc.latest?.open_slots ?? "");
  const [from, setFrom]   = React.useState(svc.latest?.available_from || "");
  const [note, setNote]   = React.useState("");

  const color   = EFFECTIVE_COLOR[svc.effective_status] || "gray";
  const overdue = svc.effective_status === "stale" || svc.effective_status === "unknown";
  const extra   = () => ({
    open_slots: slots === "" ? undefined : Number(slots),
    available_from: from,
    note,
  });

  return (
    <div className="card" style={{
      padding:'14px 15px', borderRadius:14,
      border: overdue ? '1.5px solid var(--orange-300)' : '1px solid var(--border)',
    }}>
      <div style={{display:'flex', alignItems:'center', gap:9, marginBottom:3}}>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:14.5, fontWeight:800, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
            {svc.service_label}
          </div>
          <div className="muted" style={{fontSize:11, marginTop:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
            {showOrg && svc.organization_name ? `${svc.organization_name} · ` : ''}
            {svc.care_track_label}
            {svc.capacity_total ? ` · 정원 ${svc.capacity_total}` : ''}
          </div>
        </div>
        <span className={"badge badge-" + color} style={{fontSize:11, flex:'0 0 auto'}}>{svc.status_label}</span>
      </div>

      <div className="muted" style={{fontSize:11, marginBottom:10}}>
        {freshnessText(svc)}
        {svc.latest?.open_slots != null && ` · 여유 ${svc.latest.open_slots}명`}
        {svc.operating_days?.length > 0 && ` · ${svc.operating_days.map(d => DAY_LABEL[d] || d).join('')}`}
        {svc.visit_time_slots?.length > 0 && ` · ${svc.visit_time_slots.map(s => SLOT_LABEL[s] || s).join('·')}`}
      </div>

      {/* 한 번의 탭으로 끝내는 주 동작. 큰 버튼 3개. */}
      <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:7}}>
        {STATUS_ORDER.map(st => {
          const m = STATUS_META[st];
          const on = svc.latest?.status === st && !overdue;
          return (
            <button key={st} type="button" disabled={busy}
              onClick={() => onReport(st, open ? extra() : undefined)}
              aria-pressed={on}
              title={m.desc}
              style={{
                padding:'11px 4px', borderRadius:11, cursor: busy ? 'wait' : 'pointer',
                border: `1.5px solid var(--${m.color}-${on ? '600' : '100'})`,
                background: on ? `var(--${m.color}-500)` : 'var(--surface)',
                color: on ? '#fff' : `var(--${m.color}-700)`,
                fontWeight:800, fontSize:13.5, lineHeight:1.25,
                opacity: busy ? 0.6 : 1,
              }}>
              {m.label}
            </button>
          );
        })}
      </div>

      <button type="button" onClick={onToggle}
        style={{
          marginTop:9, width:'100%', background:'none', border:'none', cursor:'pointer',
          fontSize:11.5, fontWeight:700, color:'var(--ink-3)', padding:'2px 0',
        }}>
        {open ? '자세히 접기 ▲' : '여유 인원 · 개시일 · 메모 ▼'}
      </button>

      {open && (
        <div style={{display:'flex', flexDirection:'column', gap:8, marginTop:8,
                     paddingTop:11, borderTop:'1px dashed var(--border-soft)'}}>
          <label style={{display:'flex', alignItems:'center', gap:9}}>
            <span className="muted" style={{fontSize:11.5, fontWeight:700, width:64, flex:'0 0 auto'}}>여유 인원</span>
            <input className="input" type="number" min="0" inputMode="numeric"
              value={slots} onChange={e => setSlots(e.target.value)}
              placeholder="예: 2" style={{flex:1, fontSize:12.5, padding:'7px 10px'}}/>
          </label>
          <label style={{display:'flex', alignItems:'center', gap:9}}>
            <span className="muted" style={{fontSize:11.5, fontWeight:700, width:64, flex:'0 0 auto'}}>개시 가능</span>
            <input className="input" type="date" min={todayYmd()}
              value={from} onChange={e => setFrom(e.target.value)}
              style={{flex:1, fontSize:12.5, padding:'7px 10px'}}/>
          </label>
          <label style={{display:'flex', alignItems:'center', gap:9}}>
            <span className="muted" style={{fontSize:11.5, fontWeight:700, width:64, flex:'0 0 auto'}}>메모</span>
            <input className="input" value={note} onChange={e => setNote(e.target.value)}
              placeholder="예: 오전만 가능" maxLength={60}
              style={{flex:1, fontSize:12.5, padding:'7px 10px'}}/>
          </label>
          <div className="muted" style={{fontSize:10.5, lineHeight:1.5}}>
            위 상태 버튼을 누르면 입력한 내용과 함께 저장됩니다.
            보고는 덮어쓰지 않고 이력으로 쌓여요.
          </div>
        </div>
      )}
    </div>
  );
}
